import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { useInputState } from '@mantine/hooks';
import { FilterAddButton, FilterAreaBackButton, FilterListModal, FilterSelector, If } from '@vision/ui/components';
import { useDataGridContext } from '@vision/ui/context';
import { FilterKey, FilterType } from '@vision/ui/enums';
import { Filter, FilterSelections } from '@vision/ui/interfaces';
import { UserFilter } from '@vision/ui/interfaces/user-filter';
import {
  formatDateWithTemplate,
  getCookieLocalizationDateFormat,
  getDefaultFilterSelections,
  getFilterSelectionLabel,
} from '@vision/ui/utils';
import { dequal } from 'dequal';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from 'react-use';
import { DataGridFilterRenderer, DataGridFiltersTargetContent } from '../DataGridFilterControls';
import { DataGridFilterDrawer } from '../DataGridFilterDrawer';
import { DataGridFilterSelectionTooltipLabel } from '../DataGridFilterSelectionTooltipLabel';
import classes from './DataGridFilters.module.css';
import clsx from 'clsx';

interface FilterWithSelected extends Filter {
  selected: boolean;
}

interface DataGridFiltersProps {
  filterSelectionsFromUrl: FilterSelections[];
  onFilterSelectionChange: (selections: FilterSelections[]) => void;
  filterKey: string;
}

function _DataGridFilters({ filterSelectionsFromUrl, onFilterSelectionChange, filterKey }: DataGridFiltersProps) {
  const { meta } = useDataGridContext();
  const { t } = useTranslation(['translation', 'data-grid']);

  const [filterSelections, setFilterSelections] = useState<FilterSelections[]>(filterSelectionsFromUrl);

  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [searchText, setSearchText] = useInputState('');

  useDeepCompareEffect(() => {
    setFilterSelections(filterSelectionsFromUrl);
  }, [filterSelectionsFromUrl]);

  const selectedFiltersWithFixedFilters = useMemo<FilterWithSelected[]>(
    () =>
      meta.filters
        .map((item) => ({
          ...item,
          selected: filterSelections.some((selectedFilter) => selectedFilter.key === item.key),
        }))
        .filter((item) => item.selected || item.fixed),
    [meta, filterSelections, expanded],
  );

  const selectedFilterCount = useMemo<number>(() => {
    return selectedFiltersWithFixedFilters.filter((filter) => filter.selected).length;
  }, [selectedFiltersWithFixedFilters]);

  const filtersToDisplay = useMemo<FilterWithSelected[]>(() => {
    const sorted = [...selectedFiltersWithFixedFilters].sort((a, b) => {
      if (a.selected === b.selected) return 0;
      return a.selected ? -1 : 1;
    });
    return sorted.slice(0, expanded ? meta.filters.length : 3);
  }, [meta, selectedFiltersWithFixedFilters, expanded]);

  const defaultSelectedFilterKeys = useMemo<string[]>(
    () =>
      meta.filters
        .filter(
          (filter) =>
            filter.key === FilterKey.STATUS &&
            filter.type === FilterType.RADIO &&
            filter.options.some((option) => option.default_selected),
        )
        .map((filter) => filter.key),
    [meta],
  );

  const handleClearFilter = (filter: Filter) => {
    const filtered = filterSelections.filter((item) => item.key !== filter.key);
    onFilterSelectionChange(filtered);
    setFilterSelections(filtered);
  };

  const handleClearAll = () => {
    const defaultSelections = getDefaultFilterSelections(meta);
    onFilterSelectionChange(defaultSelections);
    setFilterSelections(defaultSelections);
  };

  const handleUpdateFiltersFromDrawer = (selections: FilterSelections[]) => {
    if (!dequal(filterSelections, selections)) {
      setFilterSelections(selections);
      onFilterSelectionChange(selections);
    }
  };

  const handleUpdateFilterSelections = (selections: FilterSelections[]) => {
    if (!dequal(filterSelections, selections)) {
      setFilterSelections(selections);
      onFilterSelectionChange(selections);
    }

    // Close Drawer if it is open
    handleDrawerClose();
  };

  const handleDrawerClose = useCallback(() => {
    setFilterDrawerOpened(false);
  }, [setFilterDrawerOpened]);

  const handleDrawerOpen = useCallback(() => {
    setFilterDrawerOpened(true);
  }, [setFilterDrawerOpened]);

  const handleApplyFilter = (filter: UserFilter) => {
    const filterSelections = JSON.parse(filter.filter) as FilterSelections[];
    setFilterSelections(filterSelections);
    onFilterSelectionChange(filterSelections);
  };

  const displayDateArray = (filterSelectionsValue: string) => {
    const dateArray = filterSelectionsValue.split(',');
    return dateArray.map((date) => formatDateWithTemplate(new Date(Number(date)), getCookieLocalizationDateFormat()));
  };

  return (
    <>
      <div className={classes.dataGridFilters} data-testid="section-data-grid-filters">
        <Group className={classes.dataGridFiltersFixed}>
          <FilterAreaBackButton />

          <FilterAddButton
            className={classes.dataGridFiltersBtnAdd}
            count={selectedFilterCount}
            onClick={handleDrawerOpen}
            data-testid="button-filter-add"
            expanded={expanded}
            onToggleExpand={() => setExpanded(!expanded)}
          />

          {filtersToDisplay.map((filter) => {
            const isDefaultSelectedFilter = defaultSelectedFilterKeys.includes(filter.key);

            let label = '';

            if (filter.selected) {
              const filterSelectionsValue = filterSelections.find((item) => item.key === filter.key)?.value;

              if (filterSelectionsValue) {
                if (Array.isArray(filterSelectionsValue)) {
                  label = `${filterSelectionsValue
                    .map((value) => filter.options.find((option) => option.value === value)?.label || value)
                    .join(', ')}`;
                } else if (typeof filterSelectionsValue === 'string') {
                  if (filter.type === FilterType.DATEPICKER || filter.type === FilterType.RADIO_DATEPICKER) {
                    label = displayDateArray(filterSelectionsValue).join(', ');
                  } else {
                    label = getFilterSelectionLabel(filter, filterSelectionsValue);
                  }
                }
              }
            }

            return (
              <FilterSelector
                clearable={!isDefaultSelectedFilter}
                key={filter.key}
                id={filter.key}
                label={filter.label}
                value={label}
                className={classes.dataGridFiltersSelector}
                targetContent={DataGridFiltersTargetContent}
                selected={filter.selected}
                tooltipDisabled={!filter.selected}
                tooltipChildren={
                  <DataGridFilterSelectionTooltipLabel filter={filter} filterSelections={filterSelections} />
                }
                onSearch={setSearchText}
                onClear={() => handleClearFilter(filter)}
                dropdownWidth={filter.type == FilterType.DATEPICKER && 500}
                searchable={
                  filter.type !== FilterType.TEXT && filter.type !== FilterType.DATEPICKER && filter.options.length > 0
                }
              >
                <DataGridFilterRenderer
                  isDefaultSelectedFilter={isDefaultSelectedFilter}
                  filter={filter}
                  filterSelections={filterSelections}
                  onFilterChange={handleUpdateFilterSelections}
                  searchText={searchText}
                  size="xs"
                />
              </FilterSelector>
            );
          })}

          <If value={!!selectedFilterCount}>
            <Tooltip label={t('clearAll')} position="bottom">
              <ActionIcon
                className={classes.filterActionButton}
                onClick={handleClearAll}
                data-testid="button-filter-clear-all"
              >
                <FontAwesomeIcon icon={faXmark} size="sm" className={classes.dataGridFiltersBtnClearIcon} />
              </ActionIcon>
            </Tooltip>
          </If>

          <FilterListModal
            classNames={{ button: clsx(classes.filterActionButton) }}
            filterData={filterSelections}
            filterKey={filterKey}
            onApplyFilter={handleApplyFilter}
          />
        </Group>
      </div>

      <DataGridFilterDrawer
        filterSelections={filterSelections}
        opened={filterDrawerOpened}
        onClose={handleDrawerClose}
        onFilterSelectionChange={handleUpdateFiltersFromDrawer}
      />
    </>
  );
}

export const DataGridFilters = React.memo(_DataGridFilters);
